import {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useEvent } from 'react-use'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import Lenis from 'lenis'

import { useLayoutStore } from '@/store/layout'
import { useRouterStore } from '@/store/router'

import { useLenisStore } from './use-lenis-store'
import { WPPageData } from '@/types'

export const LenisContext = createContext<{ lenis?: Lenis } | undefined>(
  undefined
)

type LenisProviderProps = {
  direction: string | 'horizontal' | 'vertical'
  pageProps: WPPageData
  children: React.ReactNode
} & PropsWithChildren

const LenisProvider = ({
  direction,
  pageProps,
  children
}: LenisProviderProps) => {
  const [lenis, setLenis] = useState<Lenis>()
  const wrapper = useRef<HTMLDivElement>(null!)
  const content = useRef<HTMLDivElement>(null!)
  const needsWrapperScroll = useRef<boolean>(false)
  const previousScrollStatus = useRef<boolean | null>(null)
  const setState = useLenisStore((state) => state.setState)

  const update = useCallback(
    ({ scroll, limit, progress, direction }: Lenis) => {
      setState({ scroll, limit, progress, direction })
      ScrollTrigger.update()
    },
    [setState]
  )

  const setup = useCallback(() => {
    if (lenis) {
      lenis.destroy()
      setLenis(undefined)
    }

    window.scrollTo({ top: 0, left: 0 })

    const lenisInstance = new Lenis({
      smoothWheel: true,
      eventsTarget: document.documentElement,
      ...(needsWrapperScroll.current && {
        wrapper: wrapper.current,
        content: content.current,
        gestureOrientation: 'both',
        orientation: direction === 'horizontal' ? 'horizontal' : 'vertical'
      })
    })

    ScrollTrigger.defaults({
      scroller: needsWrapperScroll.current
        ? wrapper.current
        : document.documentElement
    })

    if (needsWrapperScroll.current) {
      ScrollTrigger.scrollerProxy(wrapper.current, {
        scrollLeft(value) {
          return arguments.length
            ? lenisInstance.scrollTo(value || 0, { immediate: true })
            : lenisInstance.scroll
        },
        getBoundingClientRect() {
          return {
            top: 0,
            left: 0,
            width: window.innerWidth,
            height: window.innerHeight
          }
        }
      })
    }

    setLenis(lenisInstance)
  }, [lenis, direction])

  const resize = useCallback(() => {
    needsWrapperScroll.current = window.innerWidth >= 1024

    if (needsWrapperScroll.current !== previousScrollStatus.current) {
      previousScrollStatus.current = needsWrapperScroll.current
      setup()
    }
  }, [setup])

  useEffect(() => {
    if (!lenis) return

    const tick = (time: number) => {
      lenis.raf(time * 1000)
    }

    gsap.ticker.add(tick)

    lenis.on('scroll', update)

    return () => {
      gsap.ticker.remove(tick)
      lenis.off('scroll', update)
      lenis.destroy()
      setLenis(undefined)
    }
  }, [lenis, update])

  useEffect(() => {
    if (!lenis) return

    const unsubscribe = useLayoutStore.subscribe(
      (state) => state.lockScroll,
      (lockScroll) => (lockScroll ? lenis.stop() : lenis.start())
    )

    return () => {
      unsubscribe()
    }
  }, [lenis])

  useEffect(() => {
    const unsubscribe = useRouterStore.subscribe(
      (state) => state.currentRoute,
      () => setState({ scroll: 0, direction: 0 })
    )

    return () => {
      unsubscribe()
    }
  }, [setState])

  useEffect(() => resize(), [resize])

  useEvent('resize', resize)

  const contextValue = useMemo(() => ({ lenis }), [lenis])

  useEffect(() => {
    resize()
  }, [pageProps])

  return (
    <LenisContext.Provider value={contextValue}>
      <div
        ref={wrapper}
        className="relative lg:fixed lg:left-0 lg:top-0 lg:h-full lg:w-full lg:overflow-auto pointer:scrollbar-hide"
      >
        <div
          className={direction === 'horizontal' ? 'lg:h-full' : 'h-auto'}
          ref={content}
        >
          {children}
        </div>
      </div>
    </LenisContext.Provider>
  )
}

export default LenisProvider
